import store from '@/store/index'
import { AUTH_CURRENT_USER } from '@/store/auth/auth.types'

export const accessLevels = {
  AL_SYSADMIN: 1,
  AL_ACM_MANAGEMENT: 2,
  AL_ACM_STAFF: 3,
  AL_PARTNER_MANAGEMENT: 4,
  AL_PARTNER_STAFF: 5,
  AL_AGENCY: 6,
  AL_CLIENT: 7
}

export const user = () => {
  return store.getters[AUTH_CURRENT_USER]
}

export const getUserClient = () => {
  return store.getters[AUTH_CURRENT_USER].clients[0]
}

export const userPermissions = () => {
  return store.getters[AUTH_CURRENT_USER].permissions
}

export const userAccessLevel = () => {
  return store.getters[AUTH_CURRENT_USER].account_type.access_level.level
}

export const checkPermission = (permission, action) => {
  let permissions = user().permissions

  if (permissions.hasOwnProperty(permission)) {
    let perm = permissions[permission]
    if (perm.actions.hasOwnProperty(action)) {
      return perm.actions[action].allow
    }
  }

  return false
}

export const can = (permission, action) => {
  return checkPermission(permission, action)
}

export const canView = (permission) => {
  return checkPermission(permission, 'view')
}

export const canEdit = (permission) => {
  return checkPermission(permission, 'edit')
}

export const canCreate = (permission) => {
  return checkPermission(permission, 'create')
}

export const canDelete = (permission) => {
  return checkPermission(permission, 'delete')
}

export const canViewAdministration = () => {
  return canView('account_types') ||
    canView('partners') ||
    canView('agencies') ||
    canView('clients') ||
    canView('users')
}

export const canViewTrackingUnits = () => {
  return canView('units') ||
    can('units', 'units_technical') ||
    can('units', 'units_basic_edit')
}

export const canViewAssets = () => {
  return canView('vehicles') || canView('drives')
}

export const accessLevelFor = (access_level) => {
  if (typeof access_level === 'string') {
    if (isNaN(parseInt(access_level))) {
      if (access_level in accessLevels) {
        return accessLevels[access_level]
      } else {
        throw new Error('Invalid access level specified.')
      }
    } else {
      return parseInt(access_level)
    }
  } else {
    return parseInt(access_level)
  }
}

export const isAbove = (level) => {
  return userAccessLevel() < accessLevelFor(level)
}

export const isBelow = (level) => {
  return userAccessLevel() > accessLevelFor(level)
}

export const isEqualTo = (level) => {
  return userAccessLevel() === accessLevelFor(level)
}

export const isSysAdmin = () => {
  return userAccessLevel() === accessLevels.AL_SYSADMIN
}

export const isAboveACM = () => {
  return userAccessLevel() < accessLevels.AL_SYSADMIN
}

export const isAboveACMStaff = () => {
  return userAccessLevel() < accessLevels.AL_ACM_STAFF
}

export const isACM = () => {
  return (
    userAccessLevel() === accessLevels.AL_ACM_MANAGEMENT ||
    userAccessLevel() === accessLevels.AL_ACM_STAFF)
}

export const isACMManagement = () => {
  return userAccessLevel() === accessLevels.AL_ACM_MANAGEMENT
}

export const isACMStaff = () => {
  return userAccessLevel() === accessLevels.AL_ACM_STAFF
}

export const isAbovePartner = () => {
  return userAccessLevel() < accessLevels.AL_PARTNER_MANAGEMENT
}

export const isBelowPartner = () => {
  return userAccessLevel() > accessLevels.AL_PARTNER_MANAGEMENT
}

export const isPartner = () => {
  return (
    userAccessLevel() === accessLevels.AL_PARTNER_MANAGEMENT ||
    userAccessLevel() === accessLevels.AL_PARTNER_STAFF)
}

export const isAccessLevelPartner = (accessLevel) => {
  return (
    accessLevel === accessLevels.AL_PARTNER_MANAGEMENT ||
    accessLevel === accessLevels.AL_PARTNER_STAFF)
}

export const isPartnerManagement = () => {
  return userAccessLevel() === accessLevels.AL_PARTNER_MANAGEMENT
}

export const isPartnerStaff = () => {
  return userAccessLevel() === accessLevels.AL_PARTNER_STAFF
}

export const isAboveAgency = () => {
  return userAccessLevel() < accessLevels.AL_AGENCY
}

export const isBelowAgency = () => {
  return userAccessLevel() > accessLevels.AL_AGENCY
}

export const isAgency = () => {
  return userAccessLevel() === accessLevels.AL_AGENCY
}

export const isAccessLevelAgency = (accessLevel) => {
  return accessLevel === accessLevels.AL_AGENCY
}

export const isAboveClient = () => {
  return userAccessLevel() < accessLevels.AL_CLIENT
}

export const isClient = () => {
  return userAccessLevel() === accessLevels.AL_CLIENT
}

export const isAccessLevelClient = (accessLevel) => {
  return accessLevel === accessLevels.AL_CLIENT
}

export const belongsToMultipleClients = () => {
  return store.getters[AUTH_CURRENT_USER].clients.length > 1
}

/**
 * Determines whether the specified access level allows
 * for partners to be selected/managed.
 *
 * @param accessLevel Number
 */
export const handlePartners = (accessLevel) => {
  return (
    isAbovePartner() && (accessLevel >= accessLevels.AL_PARTNER_MANAGEMENT)
  )
}

/**
 * Determines whether the specified access level allows
 * for agencies to be selected/managed.
 *
 * @param accessLevel Number
 */
export const handleAgencies = (accessLevel) => {
  return (
    isAboveAgency() && (accessLevel >= accessLevels.AL_AGENCY)
  )
}

/**
 * Determines whether the specified access level allows
 * for agencies to be selected/managed.
 *
 * @param accessLevel Number
 */
export const handleClients = (accessLevel) => {
  return (
    (isAboveClient() || belongsToMultipleClients) &&
    (accessLevel >= accessLevels.AL_CLIENT)
  )
}

export default {
  accessLevels,
  user,
  userPermissions,
  userAccessLevel,
  checkPermission,
  can,
  canView,
  canEdit,
  canCreate,
  canDelete,
  canViewAdministration,
  canViewTrackingUnits,
  canViewAssets,
  accessLevelFor,
  isAbove,
  isBelow,
  isEqualTo,
  isSysAdmin,
  isAboveACM,
  isAboveACMStaff,
  isACM,
  isACMManagement,
  isACMStaff,
  isAbovePartner,
  isBelowPartner,
  isPartner,
  isPartnerManagement,
  isPartnerStaff,
  isAboveAgency,
  isBelowAgency,
  isAgency,
  isAboveClient,
  isClient,
  belongsToMultipleClients,
  handlePartners
}



// WEBPACK FOOTER //
// ./src/common/security.js